// Imports
import { faPencil, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Params, useMatch, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import ButtonGroup from '../../../../../../shareables/foundation/front-end/components/buttons/group';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIButton, { APIButtonOptions } from '../../../../../../shareables/integrations/api/components/button';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetListingResponse } from '../../../../../../shareables/types/api/main/website/page/type/216/listing/get';
import useScrollToElementOnLoad from '../../../../use-scroll-to-element-on-load';
import Byline from '../shared-components/byline';
import ListingForm from './listing-form';


// Styled components
const Title = styled.h3`
	display: inline-block;
	margin-bottom: 0.25rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.5rem;
	overflow-wrap: anywhere;
`;

const ListingContainer = styled.div`
	overflow-wrap: break-word;
	contain: layout;
	
	img {
		float: right;
		max-width: 100%;
		margin-left: 1.25rem;
	}
`;

const ContactInfoHeader = styled.h4`
	margin-top: 1rem;
`;

const ButtonWrapper = styled(ButtonGroup)`
	margin-top: 1rem;
	
	svg.svg-inline--fa {
		margin-right: 0.25em;
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	storybookListing?: GetListingResponse;
}


// Function component
const Listing: React.FC<Props> = ({ pageID, pageTitle, storybookListing }) => {
	// Use state
	const [listing, setListing] = useState<GetListingResponse | null>(storybookListing ?? null);
	
	
	// Use ref
	const titleRef = useRef<HTMLHeadingElement>(null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { listingIdentifier } = useParams<Params>();
	const navigate = useNavigate();
	const isPanelOpen = useMatch('/p/:partialURI/listing/:listingIdentifier/edit') !== null;
	
	
	// Parse listing ID from params
	const listingID = listingIdentifier?.split('-').pop();
	
	
	// Function that loads the listing
	const memoizedLoadListing = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetListingResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/216/listing/${listingID ?? ''}`,
				completion: (json) => {
					setListing(json.response);
				},
			})
		);
	}, [dispatch, pageID, listingID]);
	
	
	// Load listing on mount
	useEffect(() => {
		memoizedLoadListing();
	}, [memoizedLoadListing]);
	
	
	// Scroll to listing title when listing loads
	useScrollToElementOnLoad(titleRef, listing);
	
	
	// If loading, then display nothing
	if (listing === null) {
		return null;
	}
	
	
	// Build button options
	const buttonOptions: APIButtonOptions<undefined, undefined, undefined> = {
		action: 'DELETE',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/216/listing/${listingID ?? ''}`,
		warning: {
			message: 'Deleting this listing cannot be undone',
			button: 'Yes, delete my listing',
		},
	};
	
	
	// Handle edit button click
	const handleEditButtonClick = () => {
		navigate(`listing/${listingIdentifier ?? ''}/edit`, { replace: true });
	};
	
	
	// Handle delete button click
	const handleDeleteButtonClick = () => {
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success!',
				message: 'Your listing has been deleted.',
			})
		);
		
		
		// Navigate to classifieds page
		navigate('../..', { relative: 'path' });
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Helmet title={`${listing.title} | ${pageTitle}`} />
			
			<ListingForm
				pageID={pageID}
				isPanelOpen={isPanelOpen}
				onPanelClose={() => navigate('..', { relative: 'path', replace: true })}
				onFormCompletion={memoizedLoadListing}
				listing={listing}
				listingID={listingID}
			/>
			
			<Title ref={titleRef}>{listing.title}</Title>
			<Byline post={listing} created={listing.submitted} action='posted' expiringItemLabel='Listing' />
			<ListingContainer>
				{listing.thumbnail && (
					<a href={listing.thumbnail.replace('-thumbnail', '')} target='_blank' rel='noreferrer'>
						<img src={listing.thumbnail} alt='View full size' />
					</a>
				)}
				{listing.text.split('\n').map((paragraph, i) => (
					<React.Fragment key={i}>
						{paragraph}
						<br />
					</React.Fragment>
				))}
				
				{listing.contactInfo && (
					<React.Fragment>
						<ContactInfoHeader>Contact information:</ContactInfoHeader>
						{listing.contactInfo.split('\n').map((paragraph, i) => (
							<React.Fragment key={i}>
								{paragraph}
								<br />
							</React.Fragment>
						))}
					</React.Fragment>
				)}
				
				{listing.canEdit && (
					<ButtonWrapper>
						<APIButton options={buttonOptions} color='danger' smallest onCompletion={handleDeleteButtonClick}>
							<FontAwesomeIcon icon={faTrash} />
							Delete my listing
						</APIButton>
						<Button type='button' color='primary' smallest onClick={handleEditButtonClick}>
							<FontAwesomeIcon icon={faPencil} />
							Edit my listing
						</Button>
					</ButtonWrapper>
				)}
			</ListingContainer>
		</React.Fragment>
	);
};

export default Listing;
